import type { TFunction } from 'i18next';

import type { FormProblem, Refusal } from '../domain/refusal';
import type { CodeOnlyReason } from '../hooks/logic/loginMachine';

const SECONDS_PER_MINUTE = 60;

export const codeOnlyText = (t: TFunction, reason: CodeOnlyReason): string =>
  reason === 'USE_CODE'
    ? t('passcodeAuth.refusal.useCode', 'This account logs in with a code, not a password. Send one instead.')
    : t('passcodeAuth.refusal.noPassword', 'No password is set for this account. Log in with a code instead.');

export const refusalText = (t: TFunction, refusal: Refusal): string => {
  switch (refusal.code) {
    case 'UNKNOWN_IDENTIFIER':
      return t('passcodeAuth.refusal.unknown', 'We don’t recognise this number or email. Ask your site admin to invite you.');
    case 'WRONG_CODE':
      return t('passcodeAuth.refusal.wrongCode', 'That code didn’t match.');
    case 'CODE_EXPIRED':
    case 'GRANT_EXPIRED':
    case 'GRANT_INVALID':
      return t('passcodeAuth.refusal.expired', 'That code has expired. Send a new one.');
    case 'LOCKED':
      return t('passcodeAuth.refusal.locked', 'Too many attempts. This account is locked for a few minutes.');
    case 'TOO_MANY_SENDS':
      return t('passcodeAuth.refusal.tooManyNetwork', 'Too many codes were asked for on this number or network. Try again in a few minutes.');
    case 'RESEND_TOO_SOON':
      return t('passcodeAuth.refusal.resendSoon', 'A code was just sent. Wait a moment before asking for another.');
    case 'NO_CODE_REQUESTED':
    case 'GRANT_REQUIRED':
      return t('passcodeAuth.refusal.noCode', 'Ask for a code first.');
    case 'CHANNEL_UNAVAILABLE':
      return t('passcodeAuth.refusal.channel', 'That channel can’t carry a code for this account.');
    case 'WRONG_PURPOSE':
      return t('passcodeAuth.refusal.wrongPurpose', 'That code was for something else. Send a new one.');
    case 'NO_VERIFIED_PHONE':
      return t('passcodeAuth.refusal.noVerifiedPhone', 'Verify your phone first, then switch where alerts arrive.');
    case 'LINK_EXPIRED':
      return t('passcodeAuth.refusal.linkExpired', 'This link has expired. Send a new code.');
    case 'NO_PASSWORD_SET':
    case 'USE_CODE':
      return codeOnlyText(t, refusal.code);
    case 'WRONG_PASSWORD':
      return t('passcodeAuth.refusal.wrongPassword', 'That password didn’t match.');
    case 'TOO_SHORT':
    case 'PASSWORD_TOO_SHORT':
    case 'MISMATCH':
      return t('passcodeAuth.refusal.passwordRules', 'The password is too short, or the two don’t match.');
    case 'PASSWORD_ALREADY_SET':
      return t('passcodeAuth.refusal.passwordAlreadySet', 'This account already has a password. Change it from your profile.');
    case 'NO_VERIFIED_CONTACT':
      return t('passcodeAuth.refusal.noVerifiedContact', 'Verify your phone or email first, so a code can reach you.');
    case 'PHONE_TAKEN':
      return t('passcodeAuth.refusal.phoneTaken', 'That number is already on another account.');
    case 'EMAIL_TAKEN':
      return t('passcodeAuth.refusal.emailTaken', 'That email is already verified on another account.');
    case 'INVALID_EMAIL':
      return t('passcodeAuth.refusal.invalidEmail', 'That doesn’t look like an email address.');
    case 'INVALID_PHONE':
      return t('passcodeAuth.refusal.invalidPhone', 'That doesn’t look like a mobile number.');
    case 'SEND_FAILED':
      return t('passcodeAuth.refusal.sendFailed', 'We couldn’t send the code just now. Try again, or pick the other channel.');
    case null:
      return refusal.message || t('common.somethingWentWrong', 'Something went wrong. Please try again.');
  }
};

export const triesLeftText = (t: TFunction, count: number): string =>
  count === 1
    ? t('passcodeAuth.code.triesLeftOne', '1 try left')
    : t('passcodeAuth.code.triesLeft', '{{count}} tries left', { count });

export const minutesOf = (seconds: number): number => Math.max(1, Math.ceil(seconds / SECONDS_PER_MINUTE));

export const codeDetailText = (t: TFunction, refusal: Refusal): string | null => {
  if (refusal.attemptsLeft === null) return null;
  if (refusal.attemptsLeft !== 1) return triesLeftText(t, refusal.attemptsLeft);
  return refusal.lockSeconds === null
    ? t('passcodeAuth.code.lastTryNoTime', 'One more wrong code locks the account.')
    : t('passcodeAuth.code.lastTry', 'One more wrong code locks the account for {{minutes}} minutes.', { minutes: minutesOf(refusal.lockSeconds) });
};

export const passwordDetailText = (t: TFunction, refusal: Refusal): string | null => {
  if (refusal.attemptsLeft === null) return null;
  if (refusal.attemptsLeft !== 1) return triesLeftText(t, refusal.attemptsLeft);
  return refusal.lockSeconds === null
    ? t('passcodeAuth.password.lastTryNoTime', 'One more wrong password locks the account.')
    : t('passcodeAuth.password.lastTry', 'One more wrong password locks the account for {{minutes}} minutes.', {
        minutes: minutesOf(refusal.lockSeconds),
      });
};

export const lockedText = (t: TFunction, minutes: number): string =>
  minutes === 1
    ? t('passcodeAuth.locked.messageForOne', 'This account is locked for 1 minute. You can try again when the timer ends.')
    : t('passcodeAuth.locked.messageFor', 'This account is locked for {{minutes}} minutes. You can try again when the timer ends.', {
        minutes,
      });

export const formProblemText = (t: TFunction, problem: FormProblem): string => {
  switch (problem.kind) {
    case 'transport':
      return t('passcodeAuth.error.noConnection', 'No connection. Check your network and try again.');
    case 'refused':
      return refusalText(t, problem.refusal);
  }
};

const GRANT_REFUSALS: readonly string[] = ['GRANT_EXPIRED', 'GRANT_INVALID', 'GRANT_REQUIRED'];

export const newPasswordFailureText = (t: TFunction, problem: FormProblem): string =>
  problem.kind === 'refused' && problem.refusal.code !== null && GRANT_REFUSALS.includes(problem.refusal.code)
    ? t('passcodeAuth.newPassword.grantExpired', 'This step timed out, so the password wasn’t saved. You’re logged in; set one from your profile any time.')
    : formProblemText(t, problem);
